import { PUBLIC_CHANNELS, type ConversationListItemDTO } from '@sac/contracts'
import { CONVERSATION_TOPICS } from '@sac/contracts'
import { CustomerAvatar } from '../CustomerAvatar'
import { ChannelMark, Icon } from '../icons'

function timeLabel(iso: string): string {
  const d = new Date(iso)
  const now = new Date()
  const sameDay = d.toDateString() === now.toDateString()
  if (sameDay) return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
  const yesterday = new Date(now)
  yesterday.setDate(now.getDate() - 1)
  if (d.toDateString() === yesterday.toDateString()) return 'Ontem'
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })
}

function waitLabel(iso: string): string {
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60_000)
  if (mins < 1) return 'agora'
  if (mins < 60) return `${mins} min`
  const h = Math.floor(mins / 60)
  const m = mins % 60
  return m ? `${h}h ${m}min` : `${h}h`
}

export function ConversationCard({
  item,
  active,
  onClick,
}: {
  item: ConversationListItemDTO
  active: boolean
  onClick: () => void
}) {
  // Canais em que o mesmo logo cobre duas coisas: pré-venda pública e atendimento privado.
  const KIND: Partial<Record<string, string>> = {
    mercadolivre_question: 'Pergunta',
    mercadolivre: 'Mensagem',
    tiktok_comment: 'Comentário',
    tiktok: 'Mensagem',
  }
  const mlKind = KIND[item.channelType] ?? null
  // Pública (pergunta do ML, comentário do TikTok) e sem resposta: destacada.
  const publicOpen = PUBLIC_CHANNELS.includes(item.channelType) && item.status !== 'resolved'
  const showWait = Boolean(item.waitingSince && (item.slaBreached || item.status !== 'resolved'))
  // Only shown while someone is actually waiting. `waitingSince` is null once a reply has gone
  // out — including one written in the marketplace's own console — so an answered thread stops
  // advertising a wait that nobody is doing.
  const waitEl = !showWait ? null : item.slaBreached ? (
    <span className="sla warn">
      <Icon name="warn" size={11} />
      {waitLabel(item.waitingSince!)} sem resposta
    </span>
  ) : (
    <span className="sla">{waitLabel(item.waitingSince!)}</span>
  )
  return (
    <div className={`conv-card ${active ? 'on' : ''} ${item.unreadCount ? 'unread' : ''}`} onClick={onClick}>
      <div className="cc-av">
        <CustomerAvatar seed={item.customerId} color={item.customerColor} name={item.customerName} size={38} />
        <span className="cc-ch">
          <ChannelMark channel={item.channelType} size={17} business={item.business} />
        </span>
        {/* O assunto, espelhando o canal no outro canto do avatar. Só aparece quando o
            classificador teve certeza — sem etiqueta é melhor que etiqueta errada, porque quem
            varre a caixa de entrada com o olho confia no que vê sem abrir. */}
        {item.topic && (
          <span className="cc-topic" title={CONVERSATION_TOPICS[item.topic].label} aria-label={CONVERSATION_TOPICS[item.topic].label}>
            {CONVERSATION_TOPICS[item.topic].emoji}
          </span>
        )}
      </div>
      <div className="cc-body">
        <div className="cc-r1">
          <span className="cc-name">{item.customerName}</span>
          <span className="cc-time">{timeLabel(item.lastMessageAt)}</span>
        </div>
        <div className="cc-prev">{item.lastMessagePreview}</div>
        <div className="cc-r3">
          {/* Mercado Livre e TikTok: Pergunta/Comentário (pública) ou Mensagem (atendimento) por extenso, logo
              ao lado do logo e colada no tempo — o logo sozinho diz "Mercado Livre" para os dois.
              Destacada enquanto a pergunta está sem resposta: é pública e o marketplace mede o
              tempo dela. */}
          {mlKind && (
            <span className="cc-kindwait">
              <span className={`cc-kind ${publicOpen ? 'open' : ''}`}>{mlKind}</span>
              {waitEl && <span className="cc-dash">–</span>}
              {waitEl}
            </span>
          )}
          {item.primaryOrderNumber && <span className="tag ord">Pedido #{item.primaryOrderNumber}</span>}
          {item.aiHandling && <span className="pin-ai">✨ IA</span>}
          {item.status === 'waiting_internal' && <span className="tag">Aguardando interno</span>}
          {item.status === 'resolved' && <span className="tag">Resolvido</span>}
          {!mlKind && waitEl}
          {/* A fired reminder is its own state, deliberately not folded into unread: unread means
              "a customer wrote and nobody answered", and this thread is the opposite — we
              replied, and a date we agreed to has now arrived. */}
          {item.reminderFired && (
            <span className="sla warn" title="Lembrete venceu — veja a nota interna">
              ⏰ Lembrete
            </span>
          )}
          {!item.reminderFired && item.reminderDueAt && (
            <span className="tag" title={`Lembrete agendado para ${new Date(item.reminderDueAt).toLocaleDateString('pt-BR')}`}>
              ⏰ {new Date(item.reminderDueAt).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}
            </span>
          )}
          {item.unreadCount > 0 && <span className="badge-n">{item.unreadCount}</span>}
        </div>
      </div>
    </div>
  )
}
